import React, { useState, useEffect, type RefObject } from 'react';
import { usePortal } from '../hooks/usePortal';
import { Calendar } from './Calendar';
import { MultipleCalendarsHeader } from '../molecules/MultipleCalendarsHeader';
import { CalendarContent } from '../molecules/CalendarContent';
import { TimePickerPanel } from '../molecules/TimePickerPanel';
import { getTimePickerValueForRange } from '../utils/resolveTimeConfig';
import { InnerButton } from '@/modules/InnerButton';
import { DatePickerProps, ViewMode, DateRange } from '@/shared/types/datepicker';

interface DatePickerPortalProps {
  isOpen: boolean;
  onClose: () => void;
  portalPosition: { top: number; left: number };
  portalHostRef?: RefObject<HTMLDivElement | null>;
  currentDate: Date;
  selectedDate?: Date | null;
  selectedRange?: DateRange;
  onDateSelect: (date: Date) => void;
  onDateHover?: (date: Date | null) => void;
  onViewModeChange: (mode: ViewMode) => void;
  onNavigate: (direction: 'prev' | 'next') => void;
  onMonthSelect: (monthIndex: number) => void;
  onYearSelect: (year: number) => void;
  onGoToToday?: () => void;
  viewMode: ViewMode;
  minDate?: Date;
  maxDate?: Date;
  locale?: string;
  weekDays?: string[];
  monthNames?: string[];
  startOfWeek?: number;
  showWeekNumbers?: boolean;
  yearRange?: number;
  hoverDate?: Date | null;
  closeOnSelect?: boolean;
  autoCloseRange?: boolean;
  calendarsCount?: number;
  mode?: 'single' | 'range';
  onClickSelected?: boolean;
  onAccept?: () => void;
  onCancel?: () => void;
  renderHeader?: DatePickerProps['renderHeader'];
  renderDay?: DatePickerProps['renderDay'];
  showTime?: boolean;
  timeEditable?: boolean;
  showSeconds?: boolean;
  onTimeChange?: (date: Date | null) => void;
}

export const DatePickerPortal: React.FC<DatePickerPortalProps> = ({
  isOpen,
  onClose,
  portalPosition,
  portalHostRef,
  currentDate,
  selectedDate,
  selectedRange,
  onDateSelect,
  onDateHover,
  onViewModeChange,
  onNavigate,
  onMonthSelect,
  onYearSelect,
  onGoToToday,
  viewMode,
  minDate,
  maxDate,
  locale,
  weekDays,
  monthNames,
  startOfWeek,
  showWeekNumbers,
  yearRange,
  hoverDate,
  closeOnSelect,
  autoCloseRange,
  calendarsCount = 1,
  mode = 'single',
  onClickSelected = false,
  onAccept,
  onCancel,
  renderHeader,
  renderDay,
  showTime = false,
  timeEditable = true,
  showSeconds = true,
  onTimeChange,
}) => {
  // Estado para manejar múltiples viewModes cuando hay múltiples calendarios
  const [viewModes, setViewModes] = useState<ViewMode[]>(Array(calendarsCount).fill(viewMode));

  // Estado para manejar las fechas de cada calendario independientemente
  const [calendarDates, setCalendarDates] = useState<Date[]>(() => {
    const dates = [];
    for (let i = 0; i < calendarsCount; i++) {
      const date = new Date(currentDate);
      date.setMonth(date.getMonth() + i);
      dates.push(date);
    }
    return dates;
  });

  const { renderPortal } = usePortal(isOpen, onClose, portalPosition, 1100, portalHostRef);

  // Sincronizar viewModes cuando cambie el viewMode global
  useEffect(() => {
    setViewModes(Array(calendarsCount).fill(viewMode));
  }, [viewMode, calendarsCount]);

  // Sincronizar fechas cuando cambie la fecha global
  useEffect(() => {
    const newDates = [];
    for (let i = 0; i < calendarsCount; i++) {
      const date = new Date(currentDate);
      date.setMonth(date.getMonth() + i);
      newDates.push(date);
    }
    setCalendarDates(newDates);
  }, [currentDate, calendarsCount]);

  // Función para manejar el cambio de viewMode de un calendario específico
  const handleToggleViewMode = (calendarIndex: number) => {
    const currentViewMode = viewModes[calendarIndex];
    let newViewMode: ViewMode;

    switch (currentViewMode) {
      case 'days':
        newViewMode = 'months';
        break;
      case 'months':
        newViewMode = 'years';
        break;
      case 'years':
        newViewMode = 'days';
        break;
      default:
        newViewMode = 'days';
    }

    const newViewModes = [...viewModes];
    newViewModes[calendarIndex] = newViewMode;
    setViewModes(newViewModes);

    // NO actualizar el viewMode global para mantener independencia
  };

  // Función para manejar la selección de mes en un calendario específico
  const handleMonthSelect = (calendarIndex: number, monthIndex: number) => {
    // Cambiar la fecha del calendario específico
    const newCalendarDates = [...calendarDates];
    newCalendarDates[calendarIndex] = new Date(
      calendarDates[calendarIndex].getFullYear(),
      monthIndex,
      1
    );
    setCalendarDates(newCalendarDates);

    // Volver a vista de días para ese calendario
    const newViewModes = [...viewModes];
    newViewModes[calendarIndex] = 'days';
    setViewModes(newViewModes);

    // Llamar a la función global
    onMonthSelect?.(monthIndex);
  };

  // Función para manejar la selección de año en un calendario específico
  const handleYearSelect = (calendarIndex: number, year: number) => {
    // Cambiar la fecha del calendario específico
    const newCalendarDates = [...calendarDates];
    newCalendarDates[calendarIndex] = new Date(year, calendarDates[calendarIndex].getMonth(), 1);
    setCalendarDates(newCalendarDates);

    // Volver a vista de meses para ese calendario
    const newViewModes = [...viewModes];
    newViewModes[calendarIndex] = 'months';
    setViewModes(newViewModes);

    // Llamar a la función global
    onYearSelect?.(year);
  };

  // Función para manejar la navegación de un calendario específico
  const handleNavigate = (direction: 'prev' | 'next') => {
    const newCalendarDates = [...calendarDates];

    newCalendarDates.forEach((date, index) => {
      const newDate = new Date(date);

      switch (viewModes[index]) {
        case 'days':
          newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
          break;
        case 'months':
          newDate.setFullYear(newDate.getFullYear() + (direction === 'next' ? 1 : -1));
          break;
        case 'years':
          newDate.setFullYear(newDate.getFullYear() + (direction === 'next' ? 12 : -12));
          break;
      }

      newCalendarDates[index] = newDate;
    });

    setCalendarDates(newCalendarDates);

    // También llamar a la función global para mantener compatibilidad
    onNavigate(direction);
  };

  return renderPortal(
    <div className="ui-relative">
      {mode === 'range' && calendarsCount > 1 ? (
        <div className="ui-overflow-hidden ui-rounded-lg ui-bg-fill ui-shadow-04">
          {/* Header compartido con títulos y botones de navegación */}
          <MultipleCalendarsHeader
            calendarDates={calendarDates}
            viewModes={viewModes}
            onNavigate={handleNavigate}
            onToggleViewMode={handleToggleViewMode}
            onGoToToday={onGoToToday}
            monthNames={monthNames}
            yearRange={yearRange}
            renderHeader={renderHeader}
          />

          {/* Contenido de los calendarios */}
          <div className="ui-flex ui-flex-col ui-flex-wrap ui-gap-4 ui-p-4 md:ui-flex-row">
            {calendarDates.map((date, index) => (
              <div key={index} className="ui-flex-shrink-0">
                <div className="ui-w-full ui-overflow-hidden ui-rounded-lg ui-bg-fill">
                  <CalendarContent
                    currentDate={date}
                    selectedDate={selectedDate}
                    selectedRange={selectedRange}
                    onDateSelect={onDateSelect}
                    onDateHover={onDateHover}
                    minDate={minDate}
                    maxDate={maxDate}
                    viewMode={viewModes[index]}
                    weekDays={weekDays}
                    monthNames={monthNames}
                    startOfWeek={startOfWeek as 0 | 1 | 2 | 3 | 4 | 5 | 6}
                    showWeekNumbers={showWeekNumbers}
                    yearRange={yearRange}
                    hoverDate={hoverDate}
                    onMonthSelect={(monthIndex) => handleMonthSelect(index, monthIndex)}
                    onYearSelect={(year) => handleYearSelect(index, year)}
                    renderDay={renderDay}
                  />
                </div>
              </div>
            ))}
          </div>

          {showTime && viewModes.every((view) => view === 'days') && (
            <TimePickerPanel
              value={
                mode === 'range'
                  ? getTimePickerValueForRange(selectedRange ?? { start: null, end: null })
                  : (selectedDate ?? null)
              }
              showSeconds={showSeconds}
              disabled={!timeEditable}
              onTimeChange={onTimeChange}
            />
          )}

          {/* Footer compartido */}
          <div className="ui-flex ui-justify-end ui-gap-2 ui-border-t ui-border-neutral-100 ui-p-3">
            <InnerButton theme="solid" size="md" color="secondary" onClick={onCancel}>
              Cancelar
            </InnerButton>
            <InnerButton size="md" onClick={onAccept}>
              Aceptar
            </InnerButton>
          </div>
        </div>
      ) : (
        <Calendar
          currentDate={currentDate}
          selectedDate={selectedDate}
          selectedRange={selectedRange}
          onDateSelect={onDateSelect}
          onDateHover={onDateHover}
          minDate={minDate}
          maxDate={maxDate}
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
          onNavigate={onNavigate}
          onMonthSelect={onMonthSelect}
          onYearSelect={onYearSelect}
          onGoToToday={onGoToToday}
          locale={locale}
          weekDays={weekDays}
          monthNames={monthNames}
          startOfWeek={startOfWeek as 0 | 1 | 2 | 3 | 4 | 5 | 6}
          showWeekNumbers={showWeekNumbers}
          yearRange={yearRange}
          hoverDate={hoverDate}
          closeOnSelect={closeOnSelect}
          autoCloseRange={autoCloseRange}
          mode={mode}
          showTime={showTime}
          timeEditable={timeEditable}
          showSeconds={showSeconds}
          onTimeChange={onTimeChange}
          onAccept={onAccept}
          onCancel={onCancel}
          renderHeader={renderHeader}
          renderDay={renderDay}
        />
      )}
    </div>
  );
};
